import React, { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import { TurfPricingDetailsProps } from "../../../Interfaces";
import { Button, Typography, Card, Divider } from "antd";
import Link from "next/link";
import { buildTurfBookingRoute, getAreaFromPath } from "../../../../utils/turfRoutes";
import { PhoneIcon } from "../../../../components/Icons";

const { Text, Title } = Typography;

export const TurfPricingDetails: React.FC<TurfPricingDetailsProps> = ({
  data,
  dataKey,
  handleArea,
  turfName,
  city,
}) => {
  const router = useRouter();
  const [selectedArea, setSelectedArea] = useState(data[dataKey][0]);
  const [hoveredPackage, setHoveredPackage] = useState<number | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const routeArea = getAreaFromPath(router.asPath || "");
  const areaNameForRoute = routeArea || data?.areaName || null;

  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleBookNow = (packageIndex: number, area: string, price: string) => {
    if (turfName && city) {
      // Get the pitch name from the selected package
      const selectedPackage = data[dataKey][packageIndex];
      const pitchName = selectedPackage?.pitchName || data[dataKey][0]?.pitchName;
      const sport = selectedPackage?.sport || data[dataKey][0]?.sport;
      
      if (pitchName) {
        let bookingUrl = `${buildTurfBookingRoute({
          city: city || "",
          userName: turfName,
          pitchName,
          areaName: areaNameForRoute,
        })}?area=${encodeURIComponent(area)}&price=${encodeURIComponent(price)}`;
        if (sport) bookingUrl += `&sport=${encodeURIComponent(sport)}`;
        router.push(bookingUrl);
      }
    }
  };

  useEffect(() => {
    setSelectedArea(data[dataKey][0]);
  }, [dataKey, data]);

  const hasMultiplePitches = data[dataKey].length > 1;

  const highestPitch = data[dataKey].reduce((max: any, pitch: any) => {
    const maxHeight = max?.pitchDimensions?.height;
    const pitchHeight = pitch?.pitchDimensions?.height;
    return maxHeight &&
      pitchHeight &&
      parseFloat(maxHeight) > parseFloat(pitchHeight)
      ? max
      : pitch;
  }, data[dataKey][0]);

  const lowestPricePitch = data[dataKey].reduce((min: any, pitch: any) => {
    const minPrice = min?.price?.weekday?.regularHours;
    const pitchPrice = pitch?.price?.weekday?.regularHours;
    if (!minPrice && !pitchPrice) return min;
    if (!minPrice) return pitch;
    if (!pitchPrice) return min;
    return parseFloat(pitchPrice) < parseFloat(minPrice) ? pitch : min;
  }, data[dataKey][0]);

  const handleTurfCardClick = (index: number) => {
    const selectedPackage = data[dataKey][index];
    setSelectedPackage(selectedPackage);
    if (handleArea) {
      handleArea(selectedPackage);
    }
  };
  const [showFullAddress, setShowFullAddress] = useState(false);

  const locationLine = useMemo(() => {
    const area = typeof data?.areaName === "string" ? data.areaName.trim() : "";
    if (area) return area;
    const addr = typeof data?.address === "string" ? data.address.trim() : "";
    return addr;
  }, [data?.areaName, data?.address]);

  const locationCollapsed =
    locationLine.length > 28 ? `${locationLine.slice(0, 28)}...` : locationLine;
  const locationNeedsToggle = locationLine.length > 28;

  useEffect(() => {
    setShowFullAddress(false);
  }, [locationLine]);

  const contactPhones = useMemo(() => {
    const key = data?.partnership ? "displayContact" : "actualContact";
    const raw = data?.[key];
    if (typeof raw !== "string") return [];
    return raw.split("/").map((s: string) => s.trim()).filter(Boolean);
  }, [data?.partnership, data?.displayContact, data?.actualContact]);

  const primaryTelHref = useMemo(() => {
    if (contactPhones.length === 0) return null;
    const digits = contactPhones[0].replace(/^\+91\s?/, "").replace(/\s/g, "");
    if (!digits) return null;
    return `tel:+91${digits}`;
  }, [contactPhones]);

  const callIconLink =
    isMobile && primaryTelHref ? (
      <a
        href={primaryTelHref}
        onClick={(e) => e.stopPropagation()}
        aria-label={`Call +91 ${contactPhones[0]?.replace(/^\+91\s?/, "")}`}
        style={{
          flexShrink: 0,
        display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 44,
          height: 44,
          borderRadius: "50%",
          background: "#f0f5ff",
          color: "#0958d9",
        }}
      >
        <PhoneIcon size={22} color="#0958d9" />
      </a>
    ) : null;

  return (
    <div className="turf-data">
      {hasMultiplePitches ? (
        <>
          {isMobile && callIconLink ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Text strong style={{ marginRight: 4 }}>
                    {showFullAddress ? locationLine : locationCollapsed}
                  </Text>
                  {locationNeedsToggle &&
                    (!showFullAddress ? (
                      <Button
                        type="link"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowFullAddress(true);
                        }}
                        style={{ padding: 0, lineHeight: 1, color: '#0958d9' }}
                        aria-label="Show full address"
                      >
                        See More
                      </Button>
                    ) : (
                      <Button
                        type="link"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowFullAddress(false);
                        }}
                        style={{ padding: 0, lineHeight: 1, color: '#0958d9' }}
                        aria-label="Show less address"
                      >
                        See Less
                      </Button>
                    ))}
                </div>
                {lowestPricePitch?.price?.weekday?.regularHours && (
                  <Text
                    strong
                    style={{ fontSize: '16px', display: 'block', marginTop: '0', marginBottom: '0' }}
                  >
                    Starting @ {lowestPricePitch.price.weekday.regularHours}/hr
                  </Text>
                )}
              </div>
              {callIconLink}
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Text strong style={{ marginRight: 4 }}>
                  {showFullAddress ? locationLine : locationCollapsed}
                </Text>
                {locationNeedsToggle &&
                  (!showFullAddress ? (
                    <Button
                      type="link"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowFullAddress(true);
                      }}
                      style={{ padding: 0, lineHeight: 1, color: '#0958d9' }}
                      aria-label="Show full address"
                    >
                      See More
                    </Button>
                  ) : (
                    <Button
                      type="link"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowFullAddress(false);
                      }}
                      style={{ padding: 0, lineHeight: 1, color: '#0958d9' }}
                      aria-label="Show less address"
                    >
                      See Less
                    </Button>
                  ))}
              </div>
              {lowestPricePitch?.price?.weekday?.regularHours && (
                <Text
                  strong
                  style={{ fontSize: '16px', display: 'block', marginTop: '0', marginBottom: '0' }}
                >
                  Starting @ {lowestPricePitch.price.weekday.regularHours}/hr
                </Text>
              )}
            </>
          )}
        </>
      ) : (
        <div className="single-pitch-details">
          {isMobile && callIconLink ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Text strong style={{ marginRight: 4 }}>
                    {showFullAddress ? locationLine : locationCollapsed}
                  </Text>
                  {locationNeedsToggle &&
                    (!showFullAddress ? (
                      <Button
                        type="link"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowFullAddress(true);
                        }}
                        style={{ padding: 0, lineHeight: 1, color: '#0958d9' }}
                        aria-label="Show full address"
                      >
                        See More
                      </Button>
                    ) : (
                      <Button
                        type="link"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowFullAddress(false);
                        }}
                        style={{ padding: 0, lineHeight: 1, color: '#0958d9' }}
                        aria-label="Show less address"
                      >
                        See Less
                      </Button>
                    ))}
                </div>
                {selectedArea?.price?.weekday?.regularHours && (
                  <Text
                    strong
                    style={{ fontSize: '16px', display: 'block', marginTop: '0', marginBottom: '0' }}
                  >
                    Starting @ {selectedArea.price.weekday.regularHours}/hr
                  </Text>
                )}
              </div>
              {callIconLink}
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Text strong style={{ marginRight: 4 }}>
                  {showFullAddress ? locationLine : locationCollapsed}
                </Text>
                {locationNeedsToggle &&
                  (!showFullAddress ? (
                    <Button
                      type="link"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowFullAddress(true);
                      }}
                      style={{ padding: 0, lineHeight: 1, color: '#0958d9' }}
                      aria-label="Show full address"
                    >
                      See More
                    </Button>
                  ) : (
                    <Button
                      type="link"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowFullAddress(false);
                      }}
                      style={{ padding: 0, lineHeight: 1, color: '#0958d9' }}
                      aria-label="Show less address"
                    >
                      See Less
                    </Button>
                  ))}
              </div>
              {selectedArea?.price?.weekday?.regularHours && (
                <Text
                  strong
                  style={{ fontSize: '16px', display: 'block', marginTop: '0', marginBottom: '0' }}
                >
                  Starting @ {selectedArea.price.weekday.regularHours}/hr
                </Text>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
